import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Clock } from 'lucide-react';
import { api } from '../../services/api';
import DetailSection from '../details/DetailSection';
import ForwardLink from '../details/ForwardLink';
import Kpi from '../ui/Kpi';
import StateBlock from '../ui/StateBlock';
import EmptyState from '../EmptyState';
import { hoursText } from './projectModel';
import { WorkLogsExplorer } from '../worklogs/GroupedWorkLogs';

/**
 * P-04/C (D-39) — the "שעות" tab: planned vs actual and the difference at the
 * top; by employee (hours, percent); by milestone (planned, actual) with a
 * "ללא אבן דרך" row at the bottom; a link to the project's work records.
 */
export default function ProjectHoursTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['project-hours', projectId],
    queryFn: () => api.getProjectHours(projectId),
  });
  const h = data?.data;

  if (isLoading) return <StateBlock kind="loading" />;
  if (!h) return <StateBlock kind="error" error={error} title={t('projectPage.hours.loadError')} onRetry={() => refetch()} retrying={isFetching} />;

  const planned = h.planned_hours === null ? t('projectPage.notDefined') : hoursText(h.planned_hours);
  const diff = h.difference === null ? '—' : hoursText(h.difference);
  const th = 'px-4 py-2 text-start text-xs font-medium text-surface-600';
  const td = 'px-4 py-2 text-sm text-surface-900';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi label={t('projectPage.hours.planned')} value={planned} />
        <Kpi label={t('projectPage.hours.actual')} value={hoursText(h.actual_hours)} />
        <Kpi label={t('projectPage.hours.difference')} value={diff} />
      </div>

      <DetailSection
        title={t('projectPage.hours.byEmployee')}
        actions={<ForwardLink to={`/work-logs?project=${projectId}`}>{t('projectPage.hours.allRecords')}</ForwardLink>}
        flush
      >
        {h.by_employee.length === 0 ? (
          <EmptyState icon={Clock} title={t('projectPage.hours.none')} />
        ) : (
          <table className="w-full">
            <caption className="sr-only">{t('projectPage.hours.byEmployee')}</caption>
            <thead className="border-b border-surface-200">
              <tr>
                <th scope="col" className={th}>{t('projectPage.hours.employee')}</th>
                <th scope="col" className={`${th} text-end`}>{t('projectPage.hours.hours')}</th>
                <th scope="col" className={`${th} text-end`}>{t('projectPage.hours.percent')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-100">
              {h.by_employee.map((e) => (
                <tr key={e.user_id}>
                  <td className={td}>{e.name ?? '—'}</td>
                  <td className={`${td} text-end tabular-nums`}>{hoursText(e.hours)}</td>
                  <td className={`${td} text-end tabular-nums`}>{e.percent}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DetailSection>

      <DetailSection title={t('projectPage.hours.byMilestone')} flush>
        <table className="w-full">
          <caption className="sr-only">{t('projectPage.hours.byMilestone')}</caption>
          <thead className="border-b border-surface-200">
            <tr>
              <th scope="col" className={th}>{t('projectPage.hours.milestone')}</th>
              <th scope="col" className={`${th} text-end`}>{t('projectPage.hours.planned')}</th>
              <th scope="col" className={`${th} text-end`}>{t('projectPage.hours.actual')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-100">
            {h.by_milestone.map((m) => (
              <tr key={m.milestone_id}>
                <td className={td}>
                  {m.name}
                  {m.archived && <span className="ms-2 text-xs text-surface-500">{t('projectPage.hours.archived')}</span>}
                </td>
                <td className={`${td} text-end tabular-nums`}>{hoursText(m.planned_hours)}</td>
                <td className={`${td} text-end tabular-nums`}>{hoursText(m.actual_hours)}</td>
              </tr>
            ))}
            <tr className="bg-surface-50">
              <td className={`${td} font-medium`}>{t('projectPage.hours.noMilestone')}</td>
              <td className={`${td} text-end tabular-nums`}>{hoursText(h.no_milestone.planned_hours)}</td>
              <td className={`${td} text-end tabular-nums`}>{hoursText(h.no_milestone.actual_hours)}</td>
            </tr>
          </tbody>
        </table>
      </DetailSection>

      {/* D-319 sec.9 - the logs of this project: "קבץ לפי" + month approval + PDF (shared with the work-logs page). */}
      <DetailSection title={t('logsView.tabTitle')}>
        <WorkLogsExplorer queryKey={`project-${projectId}`} scope={{ project_id: projectId }} defaultGroupBy="worker" />
      </DetailSection>
    </div>
  );
}
